import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import { todayISO, inrWhole } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { stockService } from "@/services/stockService";
import { rawMaterialService, supplierService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import { extractUnusualRate } from "@/lib/unusualRate";
import { extractPaidBillWarning } from "@/lib/paidBillWarning";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import type { RawMaterialStock, StockBalance, RawMaterial, Supplier } from "@/types/models";
import { useIsViewer, useIsOwner, useIsManager, useIsAdmin } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import Pagination from "@/components/ui/pagination/Pagination";
import { formatQty, formatRate, displayUnit } from "@/utils/formatQty";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

type Tab = "transactions" | "balances";

/**
 * Days until this material runs out, colour-banded.
 *
 * THE number the stores screen exists for. "4,200 kg" answers nothing on its
 * own, and a min-stock level is a line somebody set once and never revisited —
 * days of cover is what decides whether to place an order today. Banded at
 * under 5 and under 10, the same thresholds the farm-register design uses
 * (05-stores-eggs.js:9).
 */
function DaysLeft({ days }: { days?: number | null }) {
  if (days == null) {
    // No NET offtake in the trailing week, so there is no burn rate to divide
    // by. Not "∞ days" — a confident answer to a question nobody asked.
    //
    // "Net", and the tooltip says so: the burn rate nets purchases out but
    // counts adjustments, so a stock-take correction upward inside the window
    // can cancel that week's consumption. "Nothing issued" would be a definite
    // claim about the farm, false in exactly the case that triggers it.
    return <span className="text-gray-400" title="No net offtake in the last 7 days">—</span>;
  }
  const tone =
    days < 5
      ? "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500"
      : days < 10
        ? "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-500"
        : "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${tone}`}>
      {days.toFixed(1)}
    </span>
  );
}

export default function StockPage() {
  // Stock valuation follows the design, which puts Avg rate and Value behind
  // owner/manager/accounts (05-stores-eggs.js:3). Quantities and days-of-cover
  // stay open to everyone — the storekeeper needs those.
  //
  // PRESENTATIONAL, not a security boundary, and worth being exact about:
  // GET /raw-material-stock/balances is open to every authenticated role and
  // still returns avg_rate and value in the JSON. Enforcing it server-side
  // here alone would be theatre — the transactions tab below already shows
  // rate_per_unit to every role, and a viewer can open the whole flock P&L.
  // If stock valuation is ever genuinely confidential, that is a decision
  // about the endpoint, not about this column.
  //
  // Hooks called UNCONDITIONALLY: `useIsOwner() || useIsManager()`
  // short-circuits, so an owner skips the later calls and the hook order
  // changes between renders.
  const isOwnerRole = useIsOwner();
  const isManagerRole = useIsManager();
  // Admin is system-wide and sees what an owner sees.
  const isAdminRole = useIsAdmin();
  const canSeeValue = isOwnerRole || isManagerRole || isAdminRole;
  const isViewer = useIsViewer();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  const [tab, setTab] = useState<Tab>("balances");

  // Transactions state
  const [entries, setEntries] = useState<RawMaterialStock[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Balances state
  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);

  // Shared
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<RawMaterialStock | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RawMaterialStock | null>(null);
  // Set when the server asked "this bill has payments — delete anyway?".
  const [paidBillWarning, setPaidBillWarning] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [filterMaterialId, setFilterMaterialId] = useState("");
  const [filterTxnType, setFilterTxnType] = useState("");
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);

  // Form state
  const [rawMaterialId, setRawMaterialId] = useState("");
  const [txnType, setTxnType] = useState<"purchase" | "adjustment">("purchase");
  const [date, setDate] = useState(todayISO());
  const [qty, setQty] = useState("");
  // NO RECEIPT CHECK HERE. This form sends a supplier NAME, not a
  // supplier_id, so it never creates a payable (stock_service requires the id)
  // — a check here could correct the silo but never the bill, which is the
  // whole point of it. It lives on the Purchases page, which does.
  const [qtyUnit, setQtyUnit] = useState<"kg" | "tonne">("kg");
  const [ratePerUnit, setRatePerUnit] = useState("");
  const [rateUnit, setRateUnit] = useState<"kg" | "tonne">("kg");
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  // Set when the server asked "is this rate right?"; the checkbox answers it.
  const [unusualRateTypical, setUnusualRateTypical] = useState<number | null>(null);
  const [confirmUnusualRate, setConfirmUnusualRate] = useState(false);
  // A confirmation answers the question about ONE rate; new figures are asked about again.
  useEffect(() => {
    setConfirmUnusualRate(false);
    setUnusualRateTypical(null);
  }, [ratePerUnit, rateUnit, rawMaterialId]);

  const filteredSuppliers = useMemo(() => {
    if (!rawMaterialId) return suppliers;
    const matId = parseInt(rawMaterialId);
    return suppliers.filter((s) => s.material_ids?.includes(matId));
  }, [suppliers, rawMaterialId]);

  const loadEntries = async (fromDate?: string, toDate?: string, newOffset?: number) => {
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      if (filterMaterialId) params.raw_material_id = parseInt(filterMaterialId);
      if (filterTxnType) params.txn_type = filterTxnType;
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await stockService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as RawMaterialStock[]);
        setTotal((res as unknown as RawMaterialStock[]).length);
      } else {
        const paginated = res as unknown as {
          data: RawMaterialStock[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadBalances = async () => {
    setBalancesLoading(true);
    try {
      const data = await stockService.getBalances();
      setBalances(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setBalancesLoading(false);
    }
  };

  const loadMaterials = async () => {
    try {
      const data = await rawMaterialService.list();
      setMaterials(data as RawMaterial[]);
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadMaterials();
    loadEntries();
    supplierService.list().then((data) => setSuppliers((data as Supplier[]).filter(s => s.status === "active"))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    loadEntries();
  }, [sortConfig]);

  useEffect(() => {
    if (tab === "balances") {
      loadBalances();
    }
  }, [tab]);

  const openCreate = () => {
    setEditing(null);
    setRawMaterialId(materials.length > 0 ? materials[0].id.toString() : "");
    setTxnType("purchase");
    setDate(todayISO());
    setQty("");
    setQtyUnit("kg");
    setRatePerUnit("");
    setRateUnit("kg");
    setSupplier("");
    setNotes("");
    setUnusualRateTypical(null);
    setConfirmUnusualRate(false);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (entry: RawMaterialStock) => {
    setEditing(entry);
    setRawMaterialId(entry.raw_material_id.toString());
    setTxnType(entry.txn_type as "purchase" | "adjustment");
    setDate(new Date(entry.date).toISOString().split("T")[0]);
    const isTon = entry.unit === "ton" || entry.unit === "tonne";
    // BILL quantity, not the accepted one. `qty` on a checked row is what was
    // accepted AFTER deductions; prefilling that and resubmitting would run
    // the check again against the already-reduced figure and deduct twice.
    const billed = entry.bill_qty ?? entry.qty;
    setQty(isTon ? (billed / 1000).toString() : billed.toString());
    setQtyUnit(isTon ? "tonne" : "kg");
    setRatePerUnit(entry.rate_per_unit != null ? (isTon ? (entry.rate_per_unit * 1000).toString() : entry.rate_per_unit.toString()) : "");
    setRateUnit(isTon ? "tonne" : "kg");
    setSupplier(entry.supplier || "");
    setNotes(entry.notes || "");
    setUnusualRateTypical(null);
    setConfirmUnusualRate(false);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      raw_material_id: parseInt(rawMaterialId),
      txn_type: txnType,
      date,
      qty: qtyUnit === "tonne" ? parseFloat(qty) * 1000 : parseFloat(qty),
      rate_per_unit: ratePerUnit ? (rateUnit === "tonne" ? parseFloat(ratePerUnit) / 1000 : parseFloat(ratePerUnit)) : undefined,
      supplier: supplier || undefined,
      notes: notes || undefined,
      confirm_unusual_rate: confirmUnusualRate || undefined,
    };

    try {
      if (editing) {
        await stockService.update(editing.id, payload);
      } else {
        await stockService.create(payload);
      }
      setModalOpen(false);
      setLoading(true);
      await loadEntries();
      if (tab === "balances") loadBalances();
    } catch (err) {
      const typical = extractUnusualRate(err);
      if (typical !== null) setUnusualRateTypical(typical);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      // A second press after the warning is the owner saying yes.
      await stockService.delete(deleteTarget.id, paidBillWarning !== null);
      setDeleteTarget(null);
      setPaidBillWarning(null);
      setLoading(true);
      await loadEntries();
      if (tab === "balances") loadBalances();
    } catch (err) {
      const warning = extractPaidBillWarning(err);
      if (warning) setPaidBillWarning(warning);
      else setError(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadEntries(from, to, 0);
  };

  const handleFilter = () => {
    setOffset(0);
    setLoading(true);
    loadEntries(undefined, undefined, 0);
  };

  // A dropdown applies the moment it changes; there is no separate Filter button.
  useDidUpdate(handleFilter, [filterMaterialId, filterTxnType]);

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadEntries(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadEntries(undefined, undefined, newOffset);
  };

  return (
    <>
      <PageMeta
        title="Stock Balances | Poultry Book"
        description="Raw material stock balances and transactions"
      />
      <PageBreadcrumb pageTitle="Stock Balances" />

      {/* Tab Toggle */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setTab("transactions")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === "transactions"
              ? "bg-brand-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
          }`}
        >
          Transactions
        </button>
        <button
          onClick={() => setTab("balances")}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === "balances"
              ? "bg-brand-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
          }`}
        >
          Stock Balances
        </button>
      </div>

      {/* Transactions Tab */}
      {tab === "transactions" && (
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Stock Transactions{" "}
              {total > 0 && (
                <span className="text-sm font-normal text-gray-500">
                  ({total})
                </span>
              )}
            </h3>
            {!isViewer && (
              <Button size="sm" onClick={openCreate}>
                Log Purchase
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
            <select
              value={filterMaterialId}
              onChange={(e) => setFilterMaterialId(e.target.value)}
              className={`${selectClass} max-w-[200px]`}
            >
              <option value="">All Materials</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <select
              value={filterTxnType}
              onChange={(e) => setFilterTxnType(e.target.value)}
              className={`${selectClass} max-w-[160px]`}
            >
              <option value="">All Types</option>
              <option value="purchase">Purchase</option>
              <option value="adjustment">Adjustment</option>
            </select>
            <DateRangeFilter onApply={handleDateRangeApply} />
          </div>

          {error && !modalOpen && (
            <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            {loading ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : entries.length === 0 && !error ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                No stock transactions in the selected period.
              </div>
            ) : entries.length === 0 ? null : (
              <Table>
                <TableHeader>
                  <TableRow className="border-t border-gray-200 dark:border-gray-800">
                    <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader label="Material" sortKey="raw_material" sortConfig={sortConfig} onSort={requestSort} />
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Type
                    </TableCell>
                    <SortableHeader label="Qty" sortKey="qty" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader label="Rate" sortKey="rate_per_unit" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader label="Supplier" sortKey="supplier" sortConfig={sortConfig} onSort={requestSort} />
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Notes
                    </TableCell>
                    {!isViewer && (
                      <TableCell
                        isHeader
                        className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                      >
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow
                      key={entry.id}
                      className="border-t border-gray-100 dark:border-gray-800"
                    >
                      <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                        {new Date(entry.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.raw_material_name}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            entry.txn_type === "purchase"
                              ? "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500"
                              : "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-500"
                          }`}
                        >
                          {entry.txn_type}
                        </span>
                      </TableCell>
                      <TableCell
                        className={`px-5 py-3 text-sm text-right font-medium ${
                          entry.qty < 0
                            ? "text-error-500 dark:text-error-400"
                            : "text-gray-800 dark:text-white/90"
                        }`}
                      >
                        {formatQty(entry.qty, entry.unit)} {displayUnit(entry.unit)}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                        {entry.rate_per_unit != null
                          ? `${formatRate(entry.rate_per_unit, entry.unit)}/${displayUnit(entry.unit)}`
                          : "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {/* The linked supplier first: the Purchases form sends supplier_id, never the free-text name. */}
                        {entry.supplier_name || entry.supplier || "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {entry.notes || "-"}
                      </TableCell>
                      {!isViewer && (
                        <TableCell className="px-5 py-3 text-right">
                          {entry.txn_type === "consume" ? (
                            // A feed batch wrote this line; the batch is what to change.
                            <span className="text-xs text-gray-400 dark:text-gray-500">via feed batch</span>
                          ) : (
                            <div className="flex items-center justify-end gap-3">
                              {/* A purchase with a supplier bill is edited where bills are: this form
                                  sends only a supplier NAME, and an edit without the supplier_id would
                                  tell the server the purchase owes no bill — deleting it. */}
                              {entry.supplier_id ? (
                                <Link
                                  to="/purchases"
                                  className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                >
                                  Edit in Purchases
                                </Link>
                              ) : (
                                <button
                                  onClick={() => openEdit(entry)}
                                  className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                onClick={() => { setPaidBillWarning(null); setDeleteTarget(entry); }}
                                className="text-sm text-error-500 hover:text-error-600 dark:text-error-400"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
        </div>
      )}

      {/* Balances Tab */}
      {tab === "balances" && (
        <div className="card">
          <div className="px-5 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Current Stock Balances
            </h3>
          </div>

          {error && (
            <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            {balancesLoading ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : balances.length === 0 && !error ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                No raw materials found. Add materials in Master Data first.
              </div>
            ) : balances.length === 0 ? null : (
              <Table>
                <TableHeader>
                  <TableRow className="border-t border-gray-200 dark:border-gray-800">
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Material
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Unit
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Current Stock
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Min Level
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Used / day
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Days left
                    </TableCell>
                    {canSeeValue && (
                      <TableCell
                        isHeader
                        className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                      >
                        Avg rate
                      </TableCell>
                    )}
                    {canSeeValue && (
                      <TableCell
                        isHeader
                        className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                      >
                        Value
                      </TableCell>
                    )}
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.map((b) => (
                    <TableRow
                      key={b.raw_material_id}
                      className={`border-t border-gray-100 dark:border-gray-800 ${
                        b.is_low_stock
                          ? "bg-error-50/50 dark:bg-error-500/5"
                          : ""
                      }`}
                    >
                      <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                        {b.raw_material_name}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {b.unit}
                      </TableCell>
                      <TableCell
                        className={`px-5 py-3 text-sm text-right font-medium ${
                          b.is_low_stock
                            ? "text-error-500 dark:text-error-400"
                            : "text-gray-800 dark:text-white/90"
                        }`}
                      >
                        {formatQty(b.current_stock, b.unit)}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                        {b.min_stock_level != null
                          ? formatQty(b.min_stock_level, b.unit)
                          : "-"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right tabular-nums text-gray-600 dark:text-gray-400">
                        {b.avg_daily_use != null ? formatQty(b.avg_daily_use, b.unit) : "—"}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-right">
                        <DaysLeft days={b.days_of_cover} />
                      </TableCell>
                      {canSeeValue && (
                        <TableCell className="px-5 py-3 text-sm text-right tabular-nums text-gray-600 dark:text-gray-400">
                          {b.avg_rate != null ? `₹${b.avg_rate.toFixed(2)}` : "—"}
                        </TableCell>
                      )}
                      {canSeeValue && (
                        <TableCell className="px-5 py-3 text-sm text-right tabular-nums text-gray-600 dark:text-gray-400">
                          {b.value != null ? inrWhole(b.value) : "—"}
                        </TableCell>
                      )}
                      <TableCell className="px-5 py-3 text-center">
                        {b.is_low_stock ? (
                          <span className="inline-block rounded-full bg-error-50 px-2 py-0.5 text-xs font-medium text-error-600 dark:bg-error-500/15 dark:text-error-500">
                            Low
                          </span>
                        ) : (
                          <span className="inline-block rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-600 dark:bg-success-500/15 dark:text-success-500">
                            OK
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Stock Entry" : "Log Stock Entry"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        {unusualRateTypical !== null && modalOpen && (
          <label className="mb-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={confirmUnusualRate}
              onChange={(e) => setConfirmUnusualRate(e.target.checked)}
            />
            <span>Yes, ₹{ratePerUnit} per {rateUnit} is right — save it anyway.</span>
          </label>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="stockDate">Date *</Label>
            <Input
              id="stockDate"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="stockMaterial">Raw Material *</Label>
            <select
              id="stockMaterial"
              value={rawMaterialId}
              onChange={(e) => { setRawMaterialId(e.target.value); setSupplier(""); }}
              required
              className={selectClass}
            >
              <option value="">Select material</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="stockTxnType">Type *</Label>
            <select
              id="stockTxnType"
              value={txnType}
              onChange={(e) =>
                setTxnType(e.target.value as "purchase" | "adjustment")
              }
              required
              className={selectClass}
            >
              <option value="purchase">Purchase</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </div>

          <div>
            <Label htmlFor="stockQty">
              Quantity *{" "}
              <span className="text-xs text-gray-400">
                {txnType === "adjustment"
                  ? "(negative for wastage)"
                  : "(must be positive)"}
              </span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="stockQty"
                type="number"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder={qtyUnit === "tonne" ? "e.g. 2" : "e.g. 500"}
                required
                step="0.001"
              />
              <select
                value={qtyUnit}
                onChange={(e) => setQtyUnit(e.target.value as "kg" | "tonne")}
                className="h-11 shrink-0 rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              >
                <option value="kg">kg</option>
                <option value="tonne">tonne</option>
              </select>
            </div>
          </div>

          {txnType === "purchase" && (
            <>
              <div>
                <Label htmlFor="stockRate">Rate per {rateUnit}</Label>
                <div className="flex gap-2">
                  <Input
                    id="stockRate"
                    type="number"
                    value={ratePerUnit}
                    onChange={(e) => setRatePerUnit(e.target.value)}
                    placeholder="e.g. 25.50"
                    min={0}
                    step="0.01"
                  />
                  <select
                    value={rateUnit}
                    onChange={(e) => setRateUnit(e.target.value as "kg" | "tonne")}
                    className="h-11 shrink-0 rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  >
                    <option value="kg">per kg</option>
                    <option value="tonne">per tonne</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="stockSupplier">Supplier *</Label>
                <select
                  id="stockSupplier"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  required
                  className={selectClass}
                >
                  <option value="">Select supplier</option>
                  {filteredSuppliers.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="stockNotes">Notes</Label>
            <textarea
              id="stockNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !rawMaterialId || !qty || (txnType === "purchase" && !supplier)}>
              {saving ? "Saving..." : editing ? "Save Changes" : "Log Entry"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Stock Entry"
        message={
          <>
            Are you sure you want to delete this stock entry? This action cannot be undone.
            {paidBillWarning && (
              <div className="mt-4 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                {paidBillWarning}
              </div>
            )}
          </>
        }
        confirmLabel={paidBillWarning ? "Delete anyway" : "Delete"}
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => { setDeleteTarget(null); setPaidBillWarning(null); }}
      />
    </>
  );
}
